import VueRouter, { NavigationGuardNext, RawLocation, Route, RouteConfig } from 'vue-router';
import { Vue } from "vue-property-decorator";
import store from "@/store";
import { Location } from "vue-router/types/router";

Vue.use(VueRouter);

const defaultRoute: Route = {
    fullPath: "/",
    hash    : "",
    matched : [],
    meta    : {},
    name    : null,
    params  : {},
    path    : "/",
    query   : {},
};
const guard = function (to: Route, from: Route, next: NavigationGuardNext<Vue>) {
    if (to.matched.length === 0) {
        return next({
            name : '404',
            query: {
                route: JSON.stringify({
                    name  : to.name,
                    query : to.query,
                    params: to.params,
                }),
            },
        });
    }

    if (
        !store.getters['auth/hasFunctionGroup']
        && store.getters['auth/current'] != null
    ) {
        return to.name === 'function-group' ? next() : next({
            name: 'function-group',
        });
    }

    const loggedIn = store.getters['auth/isLoggedIn'];
    const me = store.getters['auth/current'];
    let roleRoute = 'dashboard';

    if (
        typeof to.matched[0].meta.auth !== "undefined"
        && to.matched[0].meta.auth !== loggedIn
    ) {
        return next({ name: loggedIn ? roleRoute : 'login' });
    }

    if (loggedIn) {
        roleRoute = ['ADMIN', 'SUPER_ADMIN'].includes(me.role) ? 'divisions.index' : 'dashboard';

        const allowedRoles = to.matched[0].meta.role === 'ADMIN' ? ['ADMIN', 'SUPER_ADMIN'] : [to.matched[0].meta.role];

        if (
            typeof to.matched[0].meta.role !== 'undefined'
            && !allowedRoles.includes(me.role)
        ) {
            return next({ name: loggedIn ? roleRoute : 'login' });
        }
    }

    return next();
};

class CustomRouter extends VueRouter {
    resolve(to: RawLocation, current: Route = defaultRoute, append?: boolean): {
        location: Location,
        route: Route,
        href: string,
        normalizedTo: Location,
        resolved: Route,
    } {
        let resolved = super.resolve(to, current, append);

        guard(resolved.route, current, (route?: any) => {
            if (typeof route === "undefined") {
                return;
            }

            resolved = super.resolve(route, current, append);
        });

        return resolved;
    }
}

const routes: Array<RouteConfig> = [
    {
        path    : '/',
        redirect: '/login',
    },
    {
        path     : "/",
        component: () => import(/* webpackChunkName: "base-layout-auth" */ "@/resources/layouts/BaseLayout.vue"),
        meta     : {
            auth: false
        },
        children : [
            {
                path     : "/login",
                name     : "login",
                component: () => import(/* webpackChunkName: "login" */ "@/resources/views/Auth/Login.vue"),
            },
            {
                path     : "/admin/login",
                name     : "admin.login",
                component: () => import(/* webpackChunkName: "login" */ "@/resources/views/Admin/Auth/Login.vue"),
            },
            {
                path     : "/admin/password/forgot",
                name     : "password.forgot",
                component: () => import(/* webpackChunkName: "password-forgot" */ "@/resources/views/Admin/Auth/PasswordForgot.vue"),
            },
            {
                path     : "/admin/password/reset/:token",
                name     : "password.reset",
                component: () => import(/* webpackChunkName: "password-reset" */ "@/resources/views/Admin/Auth/PasswordReset.vue"),
            },
        ]
    },
    {
        path     : "/",
        component: () => import(/* webpackChunkName: "base-layout-logged-in" */ "@/resources/layouts/BaseLayout.vue"),
        meta     : {
            auth: true,
            role: 'USER',
        },
        children : [
            {
                path     : "/function-group",
                name     : "function-group",
                component: () => import(/* webpackChunkName: "function-groups" */ "@/resources/views/Auth/FunctionGroup.vue"),
            },
            {
                path     : "/dashboard",
                name     : "dashboard",
                component: () => import(/* webpackChunkName: "dashboard" */ "@/resources/views/Dashboard.vue"),
                meta     : {
                    breadcrumb: 'Dashboard',
                },
                children : [
                    {
                        path     : "station",
                        name     : "station.show",
                        component: () => import(/* webpackChunkName: "station-show" */ "@/resources/views/StationShow.vue"),
                        meta     : {
                            breadcrumb: 'Station',
                        },
                    },
                ],
            },
            {
                path     : "/cluster",
                name     : "cluster",
                component: () => import(/* webpackChunkName: "cluster" */ "@/resources/views/Cluster.vue"),
                meta     : {
                    breadcrumb: 'Cluster',
                },
            },
            {
                path     : "/production-orders/:productionOrder/brands",
                name     : "production-orders.brands",
                component: () => import(/* webpackChunkName: "brands" */ "@/resources/views/ProductionOrders/Brands.vue"),
                meta     : {
                    breadcrumb: 'Merken',
                },
            },
            {
                path     : "/production-orders/:productionOrder/brands/:brand/checklist",
                name     : "production-orders.checklist.show",
                component: () => import(/* webpackChunkName: "checklist" */ "@/resources/views/Checklist.vue"),
                meta     : {
                    breadcrumb: 'Checklist',
                },
            },
            {
                path     : "/projects/:projectId/brands",
                name     : "projects.brands",
                component: () => import(/* webpackChunkName: "brands" */ "@/resources/views/Projects/Brands.vue"),
                meta     : {
                    breadcrumb: 'Merken',
                },
            },
            {
                path     : "/projects/:projectId/brands/:brand/checklist",
                name     : "projects.checklist.show",
                component: () => import(/* webpackChunkName: "checklist" */ "@/resources/views/Checklist.vue"),
                meta     : {
                    breadcrumb: 'Checklist',
                },
            }
        ],
    },
    {
        path     : "/",
        component: () => import(/* webpackChunkName: "base-layout-admin" */ "@/resources/layouts/BaseLayout.vue"),
        meta     : {
            auth: true,
            role: 'ADMIN',
        },
        children : [
            {
                path     : "/stations",
                name     : "stations.index",
                component: () => import(/* webpackChunkName: "admin-stations" */ "@/resources/views/Admin/Stations/Index.vue"),
                meta     : {
                    breadcrumb: 'Stations',
                },
            },
            {
                path     : "/stations/:station",
                name     : "stations.show",
                component: () => import(/* webpackChunkName: "admin-questions" */ "@/resources/views/Admin/Questions/Index.vue"),
                meta     : {
                    breadcrumb: 'Vragen',
                },
                children : [
                    {
                        path     : "edit",
                        name     : "stations.edit",
                        component: () => import(/* webpackChunkName: "admin-stations.edit" */ "@/resources/views/Admin/Stations/Edit.vue"),
                    },
                ],
            },
            {
                path     : "/stations/:station/questions",
                name     : "questions.index",
                component: () => import(/* webpackChunkName: "admin-questions" */ "@/resources/views/Admin/Questions/Index.vue"),
                meta     : {
                    breadcrumb: 'Vragen',
                },
                children : [
                    {
                        path     : ":question/edit",
                        name     : "questions.edit",
                        component: () => import(/* webpackChunkName: "admin-questions.edit" */ "@/resources/views/Admin/Questions/CreateOrEdit.vue"),
                    },
                    {
                        path     : "create",
                        name     : "questions.create",
                        component: () => import(/* webpackChunkName: "admin-questions.edit" */ "@/resources/views/Admin/Questions/CreateOrEdit.vue"),
                    },
                ],
            },
            {
                path     : "/projects",
                name     : "projects.index",
                component: () => import(/* webpackChunkName: "admin-projects" */ "@/resources/views/Admin/Projects/Index.vue"),
                meta     : {
                    breadcrumb: 'Projecten',
                },
            },
            {
                path     : "/projects/:project/production-orders",
                name     : "production-orders.index",
                component: () => import(/* webpackChunkName: "admin-production-orders" */ "@/resources/views/Admin/ProductionOrders/Index.vue"),
                meta     : {
                    breadcrumb: 'Productie orders',
                },
            },
            {
                path     : "/projects/:project/production-orders/:productionOrder/brands",
                name     : "brands.index",
                component: () => import(/* webpackChunkName: "admin-brands" */ "@/resources/views/Admin/Brands/Index.vue"),
                meta     : {
                    breadcrumb: 'Merken',
                },
            },
            {
                path     : "/projects/:project/production-orders/:productionOrder/brands/:brand/checklists",
                name     : "checklists.index",
                component: () => import(/* webpackChunkName: "admin-checklists" */ "@/resources/views/Admin/Checklists/Index.vue"),
                meta     : {
                    breadcrumb: 'Checklists',
                },
            },
            {
                path     : "/divisions",
                name     : "divisions.index",
                component: () => import(/* webpackChunkName: "admin-divisions" */ "@/resources/views/Admin/Divisions/Index.vue"),
                meta     : {
                    breadcrumb: 'Divisions',
                },
                children : [
                    {
                        path     : "create",
                        name     : "divisions.create",
                        component: () => import(/* webpackChunkName: "admin-divisions.create" */ "@/resources/views/Admin/Divisions/Create.vue"),
                    },
                ],
            },
            {
                path     : "/divisions/:division/stations",
                name     : "divisions.stations.index",
                component: () => import(/* webpackChunkName: "admin-division-stations" */ "@/resources/views/Admin/Divisions/Stations/Index.vue"),
                meta     : {
                    breadcrumb: 'Divisions Stations',
                },
            },
        ],
    },
    {
        path     : "/",
        component: () => import(/* webpackChunkName: "base-layout-admin" */ "@/resources/layouts/BaseLayout.vue"),
        meta     : {
            auth: true,
            role: 'SUPER_ADMIN',
        },
        children : [
            {
                path     : "/users",
                name     : "users.index",
                component: () => import(/* webpackChunkName: "admin-questions" */ "@/resources/views/Admin/Users/Index.vue"),
                meta     : {
                    breadcrumb: 'Beheerders',
                },
                children : [
                    {
                        path     : "create",
                        name     : "users.create",
                        component: () => import(/* webpackChunkName: "admin-users.create-or-edit" */ "@/resources/views/Admin/Users/CreateOrEdit.vue"),
                    },
                    {
                        path     : ":user/edit",
                        name     : "users.edit",
                        component: () => import(/* webpackChunkName: "admin-users.create-or-edit" */ "@/resources/views/Admin/Users/CreateOrEdit.vue"),
                    },
                ],
            },
        ],
    },
    {
        path     : "*",
        name     : "404",
        component: () => import(/* webpackChunkName: "page-not-found" */ "@/resources/views/NotFound.vue"),
    },
];

const router = new CustomRouter({
    mode: 'history',
    routes,
});

router.beforeResolve(guard);

export default router;
